import { useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import DocsLayout from '../components/DocsLayout'
import SEO from '../components/SEO'
import { 
  FaWindows, 
  FaApple, 
  FaQuestion, 
  FaRoute, 
  FaBook,
  FaLaptop,
  FaExternalLinkAlt,
  FaCode,
  FaMicrosoft
} from 'react-icons/fa'

const tutorialLinks = [
  {
    title: "Windows 11/10 如何还原(重装)系统",
    source: "华硕官方教程",
    url: "https://www.asus.com.cn/support/faq/1013063/",
    icon: <FaWindows className="text-blue-500" />,
    category: "官方教程"
  },
  {
    title: "如何在戴尔计算机上重新安装 Windows 10",
    source: "Dell官方教程",
    url: "https://www.dell.com/support/kbdoc/zh-cn/000176966/%E5%A6%82%E4%BD%95%E5%9C%A8%E6%88%B4%E5%B0%94%E8%AE%A1%E7%AE%97%E6%9C%BA%E4%B8%8A%E9%87%8D%E6%96%B0%E5%AE%89%E8%A3%85-windows-10",
    icon: <FaLaptop className="text-blue-500" />,
    category: "官方教程"
  },
  {
    title: "U盘重装系统详细教程",
    source: "什么值得买",
    url: "https://post.smzdm.com/p/a78ee8wl/",
    icon: <FaCode className="text-green-500" />,
    category: "社区教程",
    desc: "适合小白的入门教程"
  },
  {
    title: "制作U盘系统盘及安装方法",
    source: "联想知识库",
    url: "https://robotrs.lenovo.com.cn/ZmptY2NtYW5hZ2Vy/p4data/Rdata/Rfiles/USBbootdriver.html",
    icon: <FaLaptop className="text-red-500" />,
    category: "社区教程",
    desc: "Win10/Win7通用方法"
  },
  {
    title: "如何安装Windows操作系统",
    source: "联想知识库",
    url: "https://robotrs.lenovo.com.cn/ZmptY2NtYW5hZ2Vy/p4data/Rdata/Rfiles/installWindows71.html",
    icon: <FaWindows className="text-green-500" />,
    category: "社区教程",
    desc: "Win11/10/8/7/XP"
  },
  {
    title: "如何重新安装macOS",
    source: "苹果官方",
    url: "https://support.apple.com/zh-cn/102655",
    icon: <FaApple className="text-gray-600" />,
    category: "社区教程",
    desc: "macOS官方重装指南"
  },
  {
    title: "重新安装Windows",
    source: "微软官方",
    url: "https://support.microsoft.com/zh-cn/windows/%E9%87%8D%E6%96%B0%E5%AE%89%E8%A3%85-windows-d8369486-3e33-7d9c-dccc-859e2b022fc7",
    icon: <FaMicrosoft className="text-blue-600" />,
    category: "微软官方教程",
    desc: "官方指南"
  },
  {
    title: "创建适用于Windows的安装介质",
    source: "微软官方",
    url: "https://support.microsoft.com/zh-cn/windows/%E5%88%9B%E5%BB%BA%E9%80%82%E7%94%A8%E4%BA%8E-windows-%E7%9A%84%E5%AE%89%E8%A3%85%E4%BB%8B%E8%B4%A8-99a58364-8c02-206f-aa6f-40c3b507420d",
    icon: <FaMicrosoft className="text-blue-600" />,
    category: "微软官方教程",
    desc: "官方制作工具指南"
  }
];

const guideContent = {
  windows: {
    title: "Windows 重装指南",
    steps: [
      {
        title: "准备工作",
        content: "在开始重装系统之前,需要准备以下内容:",
        details: [
          {
            subtitle: "U盘准备",
            text: "一个容量≥8GB的U盘(建议使用USB3.0以上,速度更快)"
          },
          {
            subtitle: "系统镜像",
            text: "下载纯净的Windows系统镜像(ISO文件):",
            links: [
              { name: "微软官网", url: "https://www.microsoft.com/zh-cn/software-download/windows10ISO" },
              { name: "MSDN系统库", url: "https://www.xitongku.com/" },
              { name: "HelloWindows", url: "https://hellowindows.cn/" }
            ]
          },
          {
            subtitle: "制作工具",
            text: "下载U盘启动制作工具(三选一):",
            links: [
              { name: "Rufus", url: "https://rufus.ie/zh/", desc: "轻量简洁,功能强大" },
              { name: "Ventoy", url: "https://www.ventoy.net/cn/", desc: "支持多系统,无需格式化" },
              { name: "微PE工具箱", url: "https://www.wepe.com.cn/", desc: "集成多种装机工具" }
            ]
          },
          {
            subtitle: "数据备份",
            text: "备份重要文件和数据"
          }
        ],
        icon: "📝"
      },
      {
        title: "制作启动盘",
        content: "选择一种工具制作Windows启动U盘:",
        details: [
          {
            subtitle: "使用Rufus",
            steps: [
              "插入U盘,运行Rufus",
              "选择U盘设备",
              "点击选择按钮,选择下载好的ISO文件",
              "分区类型选择GPT(新主板)或MBR(老主板)",
              "文件系统选择NTFS",
              "点击开始等待完成"
            ]
          },
          {
            subtitle: "使用Ventoy",
            steps: [
              "运行Ventoy,选择U盘",
              "点击安装并等待完成",
              "将ISO文件直接拷贝到U盘即可"
            ]
          },
          {
            subtitle: "注意事项",
            steps: [
              "制作前备份U盘数据",
              "确保电脑供电正常,避免制作过程断电",
              "制作完成后检查U盘是否可引导"
            ]
          }
        ],
        icon: "💿"
      },
      {
        title: "设置BIOS启动",
        content: "进入BIOS设置启动顺序:",
        details: [
          {
            subtitle: "进入BIOS",
            text: "开机时快速按特定按键进入BIOS:",
            steps: [
              "联想电脑: F2或Fn+F2",
              "戴尔电脑: F2或F12",
              "华硕电脑: F2或Delete",
              "惠普电脑: F10或ESC"
            ]
          },
          {
            subtitle: "启动设置",
            steps: [
              "找到Boot或启动选项",
              "将U盘设置为第一启动项",
              "关闭Secure Boot(安全启动)",
              "将启动模式改为UEFI(新主板)或Legacy(老主板)"
            ]
          },
          {
            subtitle: "保存退出",
            steps: [
              "按F10保存更改",
              "选择Yes确认退出"
            ]
          }
        ],
        icon: "⚙️"
      },
      {
        title: "安装系统",
        content: "按照向导完成Windows安装:",
        details: [
          {
            subtitle: "安装界面",
            steps: [
              "选择语言和区域设置",
              "点击现在安装"
            ]
          },
          {
            subtitle: "激活设置",
            steps: [
              "没有密钥选择我没有产品密钥",
              "选择要安装的版本(推荐专业版)"
            ]
          },
          {
            subtitle: "分区设置",
            steps: [
              "删除原有分区(注意备份)",
              "选择未分配空间",
              "点击新建创建分区",
              "建议C盘60-100GB"
            ]
          },
          {
            subtitle: "安装过程",
            steps: [
              "等待系统文件复制",
              "期间电脑会重启数次",
              "请勿断电或强制关机"
            ]
          }
        ],
        icon: "🔄"
      },
      {
        title: "安装驱动程序",
        content: "为系统安装必要的硬件驱动:",
        details: [
          {
            subtitle: "自动安装",
            steps: [
              "连接网络",
              "Windows Update自动安装基础驱动"
            ]
          },
          {
            subtitle: "手动安装",
            text: "安装特定驱动:",
            steps: [
              "声卡驱动",
              "显卡驱动",
              "网卡驱动",
              "主板芯片组驱动"
            ]
          },
          {
            subtitle: "驱动来源",
            text: "驱动下载渠道:",
            links: [
              { name: "品牌电脑官网", url: "#" },
              { name: "驱动精灵", url: "https://www.drivergenius.com/" },
              { name: "英特尔驱动", url: "https://www.intel.cn/content/www/cn/zh/download-center/home.html" },
              { name: "NVIDIA驱动", url: "https://www.nvidia.cn/Download/index.aspx?lang=cn" }
            ]
          }
        ],
        icon: "🔌"
      },
      {
        title: "系统激活",
        content: "激活Windows系统:",
        details: [
          {
            subtitle: "数字权利激活",
            text: "推荐方式:",
            links: [
              { name: "HEU KMS Activator", url: "https://github.com/zbezj/HEU_KMS_Activator" },
              { name: "云萌Windows10激活工具", url: "https://cmwtat.cloudmoe.com/cn.html" }
            ]
          },
          {
            subtitle: "正版激活",
            text: "购买正版密钥:",
            steps: [
              "微软官方商店",
              "授权代理商"
            ]
          },
          {
            subtitle: "激活检查",
            steps: [
              "设置-系统-关于",
              "查看Windows激活状态"
            ]
          },
          {
            subtitle: "注意事项",
            steps: [
              "建议使用数字权利激活",
              "避免使用不明来源的激活工具",
              "激活有问题可以重置后重试"
            ]
          }
        ],
        icon: "✅"
      },
      {
        title: "系统优化",
        content: "优化系统性能和使用体验:",
        details: [
          {
            subtitle: "系统设置",
            steps: [
              "关闭不必要的自启动项",
              "卸载预装软件",
              "调整虚拟内存",
              "关闭不必要的系统服务"
            ]
          },
          {
            subtitle: "软件安装",
            text: "安装必备软件:",
            steps: [
              "浏览器",
              "解压缩工具",
              "办公软件",
              "安全软件"
            ]
          },
          {
            subtitle: "备份保护",
            text: "系统备份:",
            steps: [
              "创建系统还原点",
              "备份重要文件"
            ],
            links: [
              { name: "AOMEI Backupper", url: "https://www.aomeitech.com/aomei-backupper.html", desc: "系统备份工具" }
            ]
          }
        ],
        icon: "🚀"
      }
    ]
  },
  mac: {
    title: "Mac 重装指南",
    steps: [
      {
        title: "准备工作",
        content: "在开始重装系统之前,需要准备以下内容:",
        details: [
          {
            subtitle: "数据备份",
            steps: [
              "使用 Time Machine 备份(推荐)",
              "iCloud 云同步重要文件",
              "手动备份到外接硬盘"
            ]
          },
          {
            subtitle: "兼容性检查",
            steps: [
              "查看设备型号",
              "检查系统要求",
              "确保有足够存储空间(建议预留40GB以上)"
            ]
          },
          {
            subtitle: "系统下载",
            text: "下载macOS系统:",
            links: [
              { name: "App Store", url: "https://apps.apple.com/cn/app/macos-sonoma/id6450717509" },
              { name: "Apple官网", url: "https://support.apple.com/zh-cn/HT211683" }
            ]
          },
          {
            subtitle: "网络准备",
            steps: [
              "确保网络稳定",
              "建议使用有线网络"
            ]
          }
        ],
        icon: "📝"
      },
      {
        title: "制作启动盘(可选)",
        content: "制作macOS启动U盘的步骤",
        details: [
          {
            subtitle: "准备U盘",
            steps: [
              "准备一个至少16GB的U盘",
              "格式化为Mac OS扩展(日志式)",
              "命名为USB(便于识别)"
            ]
          },
          {
            subtitle: "终端命令",
            text: "使用终端命令制作启动盘:",
            steps: [
              "打开终端应用",
              "根据macOS版本选择命令:",
              "Monterey: sudo /Applications/Install macOS Monterey.app/Contents/Resources/createinstallmedia --volume /Volumes/USB",
              "Big Sur: sudo /Applications/Install macOS Big Sur.app/Contents/Resources/createinstallmedia --volume /Volumes/USB",
              "Ventura: sudo /Applications/Install macOS Ventura.app/Contents/Resources/createinstallmedia --volume /Volumes/USB"
            ]
          },
          {
            subtitle: "注意事项",
            steps: [
              "制作过程中不要拔出U盘",
              "需要输入管理员密码",
              "等待进度条完成"
            ]
          }
        ],
        icon: "💿"
      },
      {
        title: "进入恢复模式",
        content: "进入macOS恢复模式的方法",
        details: [
          {
            subtitle: "Intel芯片Mac",
            steps: [
              "关机后按住Command(⌘) + R键开机",
              "等待出现苹果标志和进度条"
            ]
          },
          {
            subtitle: "M1/M2芯片Mac",
            steps: [
              "关机后长按电源键",
              "等待出现启动选项",
              "选择选项图标"
            ]
          },
          {
            subtitle: "其他恢复模式组合键",
            steps: [
              "Command + Option + R: 在线恢复最新兼容的macOS",
              "Shift + Command + Option + R: 恢复出厂预装的macOS"
            ]
          },
          {
            subtitle: "继续操作",
            steps: [
              "选择语言后点击继续"
            ]
          }
        ],
        icon: "🔄"
      },
      {
        title: "磁盘工具操作",
        content: "使用磁盘工具准备安装",
        details: [
          {
            subtitle: "打开磁盘工具",
            steps: [
              "在恢复模式中选择磁盘工具",
              "在左侧边栏选择内置硬盘"
            ]
          },
          {
            subtitle: "抹掉磁盘",
            steps: [
              "点击抹掉按钮",
              "名称: Macintosh HD",
              "格式: APFS (加密)",
              "方案: GUID分区图"
            ]
          },
          {
            subtitle: "分区建议",
            steps: [
              "单分区(推荐)",
              "如需双系统,请提前规划分区"
            ]
          },
          {
            subtitle: "完成操作",
            steps: [
              "完成后退出磁盘工具"
            ]
          }
        ],
        icon: "🗑️"
      },
      {
        title: "安装系统",
        content: "开始安装macOS系统",
        details: [
          {
            subtitle: "选择安装选项",
            steps: [
              "选择重新安装macOS",
              "点击继续",
              "同意许可协议",
              "选择安装位置(Macintosh HD)"
            ]
          },
          {
            subtitle: "安装过程",
            steps: [
              "下载系统文件(需要网络)",
              "验证下载",
              "准备安装",
              "自动重启数次"
            ]
          },
          {
            subtitle: "注意事项",
            steps: [
              "安装过程中请勿关机",
              "确保电源连接",
              "保持网络通畅"
            ]
          },
          {
            subtitle: "安装时间",
            steps: [
              "预计耗时:30-60分钟"
            ]
          }
        ],
        icon: "⚙️"
      },
      {
        title: "初始设置",
        content: "完成系统初始设置",
        details: [
          {
            subtitle: "基础设置",
            steps: [
              "选择语言和地区",
              "连接WiFi网络",
              "登录Apple ID",
              "设置用户账户"
            ]
          },
          {
            subtitle: "迁移助理",
            steps: [
              "从Time Machine备份恢复",
              "从其他Mac迁移",
              "从Windows PC迁移",
              "不传输数据(全新开始)"
            ]
          },
          {
            subtitle: "系统设置",
            steps: [
              "启用/禁用Siri",
              "配置触控板",
              "设置屏幕时间",
              "开启查找我的Mac"
            ]
          }
        ],
        icon: "⚡"
      },
      {
        title: "软件安装",
        content: "安装必要的软件",
        details: [
          {
            subtitle: "系统更新",
            steps: [
              "安装最新系统更新",
              "更新App Store应用"
            ]
          },
          {
            subtitle: "必备软件",
            links: [
              { name: "Chrome", url: "https://www.google.com/chrome/" },
              { name: "Microsoft Office", url: "https://www.office.com/" },
              { name: "The Unarchiver", url: "https://theunarchiver.com/" },
              { name: "Rectangle", url: "https://rectangleapp.com/", desc: "窗口管理" }
            ]
          },
          {
            subtitle: "开发工具(可选)",
            steps: [
              "Xcode",
              "Homebrew",
              "Visual Studio Code"
            ]
          },
          {
            subtitle: "安全软件",
            links: [
              { name: "CleanMyMac X", url: "https://macpaw.com/cleanmymac" },
              { name: "Little Snitch", url: "https://www.obdev.at/products/littlesnitch/index.html" }
            ]
          }
        ],
        icon: "📥"
      },
      {
        title: "系统优化",
        content: "优化系统性能",
        details: [
          {
            subtitle: "性能优化",
            steps: [
              "清理启动项",
              "管理登录项",
              "清理硬盘空间",
              "重置SMC和NVRAM"
            ]
          },
          {
            subtitle: "安全设置",
            steps: [
              "开启FileVault加密",
              "配置防火墙",
              "设置屏幕保护",
              "启用双重认证"
            ]
          },
          {
            subtitle: "备份计划",
            steps: [
              "设置Time Machine",
              "配置iCloud同步",
              "创建恢复分区",
              "定期备份重要数据"
            ]
          }
        ],
        icon: "🚀"
      }
    ]
  },
  faq: {
    title: "常见问题解答",
    steps: [
      {
        title: "系统无法启动",
        content: "遇到系统启动问题时的排查步骤:",
        details: [
          {
            subtitle: "黑屏问题",
            steps: [
              "检查电源连接是否正常",
              "检查显示器连接线是否松动",
              "检查内存条是否松动或损坏",
              "检查显卡是否正常工作"
            ]
          },
          {
            subtitle: "BIOS问题",
            steps: [
              "尝试不同的BIOS进入按键(F2, F10, Del, Esc等)",
              "检查键盘是否正常工作",
              "尝试清除CMOS电池"
            ]
          },
          {
            subtitle: "蓝屏问题",
            steps: [
              "记录蓝屏错误代码",
              "检查硬盘是否有坏道",
              "检查内存是否损坏",
              "检查驱动程序是否兼容"
            ]
          },
          {
            subtitle: "启动项问题",
            steps: [
              "检查BIOS中的启动顺序设置",
              "检查硬盘是否被识别",
              "检查UEFI/Legacy启动模式设置"
            ]
          }
        ],
        icon: "❌"
      },
      {
        title: "系统激活问题",
        content: "解决Windows系统激活失败的常见方法:",
        details: [
          {
            subtitle: "错误代码0xC004F074",
            text: "表示KMS服务器连接失败:",
            steps: [
              "确保网络连接正常",
              "关闭防火墙和杀毒软件",
              "尝试更换KMS服务器地址"
            ]
          },
          {
            subtitle: "错误代码0x803F7001",
            text: "表示产品密钥无法使用:",
            steps: [
              "确认产品密钥是否正确",
              "确认是否适用于当前系统版本",
              "尝试电话激活方式"
            ]
          },
          {
            subtitle: "数字权利激活失败",
            steps: [
              "确保联网状态良好",
              "更新系统至最新版本",
              "在设置中添加Microsoft账户"
            ]
          },
          {
            subtitle: "推荐激活工具",
            links: [
              { name: "HEU KMS Activator", url: "https://github.com/zbezj/HEU_KMS_Activator", desc: "安全无毒的KMS激活工具" },
              { name: "云萌Windows10激活工具", url: "https://cmwtat.cloudmoe.com/cn.html", desc: "开源数字权利激活工具" }
            ]
          }
        ],
        icon: "🔑"
      },
      {
        title: "系统卡顿问题",
        content: "解决Windows系统运行缓慢、卡顿的方法:",
        details: [
          {
            subtitle: "硬件性能问题",
            steps: [
              "检查CPU和内存使用率",
              "监测硬盘读写速度",
              "检查硬盘剩余空间(保持至少20%空闲)",
              "检查散热情况"
            ]
          },
          {
            subtitle: "系统优化方法",
            steps: [
              "关闭不必要的启动项(任务管理器-启动)",
              "卸载不常用软件和应用",
              "定期磁盘清理和碎片整理",
              "关闭视觉特效(系统-高级系统设置-性能-设置)"
            ]
          },
          {
            subtitle: "后台程序优化",
            steps: [
              "关闭不必要的Windows服务",
              "禁用资源占用高的后台程序",
              "更新显卡驱动到最新版本",
              "扫描并清除恶意软件"
            ]
          },
          {
            subtitle: "推荐优化工具",
            links: [
              { name: "Dism++", url: "https://github.com/Chuyu-Team/Dism-Multi-language", desc: "Windows系统优化工具" },
              { name: "CCleaner", url: "https://www.ccleaner.com/", desc: "系统清理和优化工具" },
              { name: "Windows10Debloater", url: "https://github.com/Sycnex/Windows10Debloater", desc: "移除Win10臃肿应用" }
            ]
          }
        ],
        icon: "🐢"
      },
      {
        title: "驱动安装问题",
        content: "解决Windows驱动程序安装失败的方法:",
        details: [
          {
            subtitle: "驱动不兼容",
            steps: [
              "确认驱动是否适用于当前系统版本",
              "下载对应硬件型号的最新驱动",
              "尝试安装旧版本驱动"
            ]
          },
          {
            subtitle: "安装失败或报错",
            steps: [
              "以管理员身份运行安装程序",
              "关闭杀毒软件和Windows Defender",
              "使用驱动程序的兼容模式安装",
              "尝试使用设备管理器手动更新驱动"
            ]
          },
          {
            subtitle: "设备管理器中有黄色感叹号",
            steps: [
              "右键问题设备-卸载设备",
              "勾选删除驱动程序选项",
              "重启电脑后重新安装驱动"
            ]
          },
          {
            subtitle: "推荐驱动工具",
            links: [
              { name: "驱动人生", url: "https://www.160.com/", desc: "自动检测缺失驱动" },
              { name: "驱动精灵", url: "https://www.drivergenius.com/", desc: "驱动安装与备份" },
              { name: "Snappy Driver Installer", url: "https://sdi-tool.org/", desc: "开源驱动安装工具" }
            ]
          }
        ],
        icon: "🔧"
      },
      {
        title: "网络连接问题",
        content: "解决Windows系统网络连接故障的方法:",
        details: [
          {
            subtitle: "无法连接到WiFi",
            steps: [
              "检查WiFi开关是否打开",
              "检查无线网卡驱动是否正确安装",
              "忘记网络后重新连接",
              "重启路由器"
            ]
          },
          {
            subtitle: "有限连接或无Internet访问",
            steps: [
              "运行Windows网络诊断(右键网络图标)",
              "重置TCP/IP协议(管理员命令行输入: netsh int ip reset)",
              "重置Winsock(管理员命令行输入: netsh winsock reset)",
              "更换DNS服务器(推荐使用8.8.8.8和8.8.4.4)"
            ]
          },
          {
            subtitle: "网速慢或不稳定",
            steps: [
              "检查其他设备是否正常",
              "关闭带宽占用大的应用和下载",
              "更改无线信道减少干扰",
              "检查网络适配器设置中的节能选项"
            ]
          },
          {
            subtitle: "常用命令",
            text: "命令行网络修复命令:",
            steps: [
              "ipconfig /release - 释放IP地址",
              "ipconfig /renew - 重新获取IP地址",
              "ipconfig /flushdns - 清除DNS缓存",
              "ping 8.8.8.8 - 测试网络连通性"
            ]
          }
        ],
        icon: "📶"
      }
    ]
  },
  flowchart: {
    title: "装机流程图",
    steps: [
      {
        title: "准备阶段",
        content: "系统重装前的准备工作:",
        details: [
          {
            subtitle: "系统下载",
            text: "从以下渠道下载系统镜像:",
            links: [
              { name: "微软官网", url: "https://www.microsoft.com/zh-cn/software-download/windows10ISO" },
              { name: "MSDN系统库", url: "https://www.xitongku.com/" },
              { name: "HelloWindows", url: "https://hellowindows.cn/" }
            ]
          },
          {
            subtitle: "U盘准备",
            steps: [
              "准备一个容量≥8GB的U盘",
              "备份U盘中的重要数据",
              "格式化U盘(可选)"
            ]
          },
          {
            subtitle: "数据备份",
            text: "备份现有系统数据:",
            steps: [
              "使用备份软件备份系统",
              "手动备份重要文件到外部存储设备",
              "记录已安装的软件和激活信息"
            ],
            links: [
              { name: "EaseUS Todo Backup", url: "https://www.easeus.com/backup-software/" }
            ]
          },
          {
            subtitle: "工具准备",
            links: [
              { name: "Ventoy", url: "https://www.ventoy.net/cn/" },
              { name: "Rufus", url: "https://rufus.ie/zh/" },
              { name: "微PE工具箱", url: "https://www.wepe.com.cn/" }
            ]
          }
        ],
        icon: "📋"
      },
      {
        title: "启动盘制作",
        content: "创建可引导的Windows系统安装U盘:",
        details: [
          {
            subtitle: "选择合适的制作工具",
            text: "根据个人需求选择合适的启动盘制作工具:",
            links: [
              { name: "Rufus", url: "https://rufus.ie/zh/", desc: "简单快速" },
              { name: "Ventoy", url: "https://www.ventoy.net/cn/", desc: "多系统支持" },
              { name: "UltraISO", url: "https://www.ultraiso.com/", desc: "功能全面" }
            ]
          },
          {
            subtitle: "Rufus制作流程",
            steps: [
              "运行Rufus软件",
              "选择U盘设备",
              "点击'选择'按钮加载ISO文件",
              "分区方案选择'GPT'(新电脑)或'MBR'(旧电脑)",
              "文件系统选择'NTFS'",
              "点击'开始'按钮并等待完成"
            ]
          },
          {
            subtitle: "Ventoy制作流程",
            steps: [
              "运行Ventoy软件",
              "选择目标U盘",
              "点击'安装'按钮",
              "等待安装完成",
              "将系统ISO文件直接复制到U盘根目录"
            ]
          },
          {
            subtitle: "注意事项",
            steps: [
              "U盘数据会被清除,请提前备份",
              "制作过程中不要拔出U盘",
              "完成后检查U盘是否能被识别"
            ]
          }
        ],
        icon: "💾"
      },
      {
        title: "BIOS设置",
        content: "配置计算机BIOS以从U盘启动:",
        details: [
          {
            subtitle: "进入BIOS的方法",
            text: "不同品牌电脑进入BIOS的按键:",
            steps: [
              "联想: F2、Fn+F2或Novo按钮",
              "戴尔: F2或F12",
              "惠普: F10或Esc",
              "华硕: F2或Delete",
              "微星: Delete",
              "宏碁: F2或Delete"
            ]
          },
          {
            subtitle: "BIOS常见设置项",
            steps: [
              "Boot Order/Sequence(启动顺序):将USB设备设为第一启动项",
              "Secure Boot(安全启动):设置为Disabled",
              "Boot Mode(启动模式):设置为UEFI(新电脑)或Legacy(旧电脑)",
              "Fast Boot(快速启动):设置为Disabled"
            ]
          },
          {
            subtitle: "一次性启动菜单",
            text: "不修改BIOS的临时启动方法:",
            steps: [
              "启动时按F12、F11或Esc(因品牌而异)",
              "在弹出的菜单中选择U盘启动"
            ]
          },
          {
            subtitle: "保存设置",
            steps: [
              "完成设置后按F10保存更改",
              "选择'Yes'确认",
              "系统将自动重启",
              "保持U盘插入状态"
            ]
          }
        ],
        icon: "⚙️"
      },
      {
        title: "系统安装",
        content: "按照向导完成Windows系统安装:",
        details: [
          {
            subtitle: "启动到安装程序",
            steps: [
              "从U盘启动电脑",
              "出现'Press any key to boot from CD or DVD'提示时按任意键",
              "等待Windows安装程序加载"
            ]
          },
          {
            subtitle: "Windows安装向导",
            steps: [
              "选择语言、时间和键盘输入法",
              "点击'现在安装'",
              "输入产品密钥或选择'我没有产品密钥'",
              "选择要安装的Windows版本(推荐专业版)",
              "接受许可条款"
            ]
          },
          {
            subtitle: "分区设置",
            steps: [
              "选择'自定义安装'",
              "删除现有分区(先备份数据)",
              "创建新分区:C盘推荐80-120GB",
              "格式化分区",
              "选择分区并继续安装"
            ]
          },
          {
            subtitle: "安装过程与初始设置",
            steps: [
              "等待文件复制和安装(15-30分钟)",
              "系统将自动重启几次",
              "按照提示完成区域设置、账户创建、隐私选项等",
              "设置中尽量减少数据收集选项"
            ]
          }
        ],
        icon: "🔄"
      },
      {
        title: "系统配置",
        content: "安装完成后的系统配置工作:",
        details: [
          {
            subtitle: "驱动程序安装",
            steps: [
              "检查设备管理器中的未知设备",
              "安装显卡驱动(NVIDIA/AMD/Intel)",
              "安装声卡、网卡驱动",
              "安装主板芯片组驱动",
              "安装其他外设驱动"
            ],
            links: [
              { name: "驱动精灵", url: "https://www.drivergenius.com/" },
              { name: "英特尔驱动", url: "https://www.intel.cn/content/www/cn/zh/download-center/home.html" },
              { name: "NVIDIA驱动", url: "https://www.nvidia.cn/Download/index.aspx?lang=cn" },
              { name: "AMD驱动", url: "https://www.amd.com/zh-hans/support" }
            ]
          },
          {
            subtitle: "必备软件安装",
            text: "安装基础软件:",
            steps: [
              "浏览器:Chrome、Edge或Firefox",
              "解压软件:7-Zip或WinRAR",
              "多媒体播放器:VLC或PotPlayer",
              "办公软件:Microsoft Office或WPS Office",
              "PDF阅读器:Adobe Reader或Foxit Reader"
            ]
          },
          {
            subtitle: "系统更新",
            steps: [
              "打开Windows Update",
              "检查并安装所有可用更新",
              "重启系统应用更新"
            ]
          },
          {
            subtitle: "系统激活",
            text: "激活Windows系统:",
            links: [
              { name: "HEU KMS Activator", url: "https://github.com/zbezj/HEU_KMS_Activator" },
              { name: "云萌激活工具", url: "https://cmwtat.cloudmoe.com/cn.html" }
            ]
          }
        ],
        icon: "🛠️"
      },
      {
        title: "系统优化",
        content: "提升系统性能和使用体验的优化措施:",
        details: [
          {
            subtitle: "启动项优化",
            steps: [
              "打开任务管理器-启动选项卡",
              "禁用不必要的自启动程序",
              "使用CCleaner管理启动项",
              "检查计划任务中的自动运行项"
            ]
          },
          {
            subtitle: "系统服务优化",
            steps: [
              "打开服务管理器(services.msc)",
              "将不必要的服务设为手动或禁用",
              "常见可禁用服务:Windows Search、Superfetch等"
            ]
          },
          {
            subtitle: "磁盘优化",
            steps: [
              "定期磁盘清理(右键磁盘-属性-磁盘清理)",
              "设置合理的虚拟内存(建议为物理内存的1.5倍)",
              "禁用休眠功能以释放磁盘空间(powercfg -h off)",
              "定期碎片整理(固态硬盘不需要)"
            ]
          },
          {
            subtitle: "隐私与安全设置",
            steps: [
              "关闭不必要的遥测和数据收集",
              "调整Windows Defender设置",
              "配置Windows防火墙",
              "设置系统还原点以备不时之需"
            ],
            links: [
              { name: "O&O ShutUp10", url: "https://www.oo-software.com/en/shutup10", desc: "隐私保护工具" },
              { name: "Windows10Debloater", url: "https://github.com/Sycnex/Windows10Debloater", desc: "移除臃肿应用" }
            ]
          }
        ],
        icon: "🚀"
      }
    ]
  },
  tutorials: {
    title: "精选图文教程",
    content: "来自官方渠道和社区的详细图文教程，适合各种品牌电脑用户参考。",
    links: tutorialLinks
  }
}

const sidebarItems = [
  { title: "Windows重装指南", path: "/guide/windows", icon: <FaWindows /> },
  { title: "Mac重装指南", path: "/guide/mac", icon: <FaApple /> },
  { title: "常见问题解答", path: "/guide/faq", icon: <FaQuestion /> },
  { title: "装机流程图", path: "/guide/flowchart", icon: <FaRoute /> },
  { title: "精选图文教程", path: "/guide/tutorials", icon: <FaBook /> }
]

const Guide = () => {
  const { id = "windows" } = useParams()
  const currentGuide = guideContent[id] || guideContent.windows

  const seoData = {
    windows: {
      title: "Windows系统重装指南 | 详细教程与一键重装系统方法",
      description: "专业详细的Windows 10/11系统重装教程，包含U盘启动盘制作、系统安装、驱动安装、系统激活和优化等步骤，让你轻松完成Windows系统重装。",
      keywords: "Windows重装系统指南,Windows 10重装系统,Windows 11重装系统,系统安装教程,驱动安装,系统激活,系统优化,U盘启动,一键重装系统"
    },
    mac: {
      title: "Mac系统重装指南 | macOS系统安装教程与疑难解答",
      description: "专业完整的macOS系统重装教程，包括系统备份、制作启动盘、系统安装和软件配置等步骤，帮助你安全快速地重装Mac系统。",
      keywords: "Mac重装系统指南,macOS安装教程,系统备份,启动盘制作,macOS软件配置,Time Machine,Mac系统还原,苹果电脑重装"
    },
    faq: {
      title: "电脑装机常见问题解答 | 系统重装疑难问题汇总",
      description: "全面系统重装过程中的常见问题解答，包括启动问题、驱动问题、激活问题等疑难解答，帮你解决重装过程中的各种问题。",
      keywords: "系统重装问题解决,系统激活问题,驱动安装问题,蓝屏问题解决,网络设置问题,系统优化技巧,重装系统常见问题,BIOS设置"
    },
    flowchart: {
      title: "电脑重装系统流程图解 | 系统安装步骤详解",
      description: "专业系统重装完整流程图解，从准备工作到系统优化的详细步骤说明，让你对整个重装过程一目了然，轻松完成系统重装。",
      keywords: "装机流程图解,系统重装步骤,装机教程图解,系统安装流程,重装准备工作,系统优化方法,一键重装流程,装机攻略"
    }
  }

  return (
    <DocsLayout sidebarItems={sidebarItems}>
      <SEO 
        title={seoData[id]?.title || "系统重装指南"}
        description={seoData[id]?.description || "专业的系统重装指南，提供Windows和Mac系统安装教程，帮助你轻松完成系统重装。"}
        keywords={seoData[id]?.keywords || "系统重装,Windows安装,Mac安装,装机教程"}
      />
      <motion.h1 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-3xl font-bold text-primary mb-8 text-center"
      >
        {currentGuide.title}
      </motion.h1>
      
      {id === 'tutorials' ? (
        <div className="space-y-6">
          <p className="text-gray-600 mb-6 text-center">{currentGuide.content}</p>
          
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4">官方品牌教程</h2>
            <div className="grid gap-6 md:grid-cols-2">
              {currentGuide.links
                .filter(link => link.category === "官方教程")
                .map((link, index) => (
                  <motion.a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="block p-6 bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center mb-3">
                      {link.icon}
                      <h3 className="ml-3 text-lg font-medium text-gray-800">{link.title}</h3>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{link.source}</span>
                      <FaExternalLinkAlt className="text-primary text-sm" />
                    </div>
                  </motion.a>
                ))}
            </div>
          </div>
          
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4">微软官方教程</h2>
            <div className="grid gap-6 md:grid-cols-2">
              {currentGuide.links
                .filter(link => link.category === "微软官方教程")
                .map((link, index) => (
                  <motion.a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="block p-6 bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center mb-3">
                      {link.icon}
                      <h3 className="ml-3 text-lg font-medium text-gray-800">{link.title}</h3>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{link.source}</span>
                      {link.desc && <span className="text-xs bg-blue-100 text-blue-700 rounded-full px-2 py-1">{link.desc}</span>}
                    </div>
                  </motion.a>
                ))}
            </div>
          </div>
          
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-4">社区精选教程</h2>
            <div className="grid gap-6 md:grid-cols-2">
              {currentGuide.links
                .filter(link => link.category === "社区教程")
                .map((link, index) => (
                  <motion.a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="block p-6 bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center mb-3">
                      {link.icon}
                      <h3 className="ml-3 text-lg font-medium text-gray-800">{link.title}</h3>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{link.source}</span>
                      {link.desc && <span className="text-xs bg-green-100 text-green-700 rounded-full px-2 py-1">{link.desc}</span>}
                    </div>
                  </motion.a>
                ))}
            </div>
          </div>
        </div>
      ) : (
        currentGuide.steps && currentGuide.steps.map((step, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="mb-8 bg-white p-6 rounded-lg shadow-sm border border-gray-200"
          >
            <div className="flex items-start mb-4">
              <div className="text-3xl mr-4">{step.icon}</div>
              <div>
                <h2 className="text-xl font-bold text-gray-800">{step.title}</h2>
                <p className="text-gray-600 mt-1">{step.content}</p>
              </div>
            </div>
            
            {step.details && step.details.map((detail, detailIndex) => (
              <div key={detailIndex} className="ml-12 mb-4">
                <h3 className="font-semibold text-gray-700 mb-2">{detail.subtitle}</h3>
                {detail.text && <p className="text-gray-600 mb-2">{detail.text}</p>}
                
                {detail.steps && (
                  <ul className="list-disc list-inside text-gray-600 space-y-1 mb-2">
                    {detail.steps.map((step, stepIndex) => (
                      <li key={stepIndex}>{step}</li>
                    ))}
                  </ul>
                )}
                
                {detail.links && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {detail.links.map((link, linkIndex) => (
                      <a
                        key={linkIndex}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm bg-blue-50 text-blue-600 px-3 py-1 rounded-full hover:bg-blue-100 transition-colors"
                      >
                        {link.name} {link.desc && <span className="text-xs text-gray-500 ml-1">({link.desc})</span>}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </motion.div>
        ))
      )}
    </DocsLayout>
  )
}

export default Guide 